import './styles/buttons.css';
import { isContractDisabled, getStatusColor } from './utils/formatters/contractStatus';
import { isValidDate } from './utils/helpers/operationHelpers';
import { mapReceivableFromApi } from './utils/mappers/dataMappers';
import { mapAcknowledgementDebtApiResponseToForm } from './utils/mappers/responseMappers';
import { buildAcknowledgementDebtPayload } from './utils/payload/acknowledgementDebtPayloadBuilder';
import { useOperationData } from './hooks/data/useOperationData';
import { useProductManagement } from './hooks/data/useProductManagement';
import { useReceivablesDownloader } from './hooks/data/useReceivablesDownloader';
import { useContractSubmit } from './hooks/contract/useContractSubmit';
import { useContractFetch } from './hooks/contract/useContractFetch';
import { useSignal } from "@preact/signals-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Auth } from "aws-amplify";
import { useCallback, useEffect, useState, Fragment } from "react";
import ConfirmationModal from "../../templates/ConfirmationModal";
import AmendmentModal from "./modals/AmendmentModal";
import PageForm from "../../templates/PageForm";
import TextEdit from "../../molecules/TextEdit";
import SelectTextField from "../../molecules/SelectTextField";
import CurrencyInput from "../../molecules/CurrencyInput";
import { applyDateMask } from '../../../utils/dateMask';
import { ErrorField } from '../../../types/ErrorField';
import { getErrorMessage } from '../../../utils/formErrorUtils';
import { FaTimes, FaSearch } from "react-icons/fa";
import BadgeStatus from "../../atoms/BadgeStatus";
import useProvinces from "../../../hooks/useProvinces";
import useCities from "../../../hooks/useCities";
import ReceivableModalView from "./modals/ReceivableModalView";

import ArtifactFilesUpload from './components/files/ArtifactFilesUpload';
import PersonSearchModal from "./modals/PersonSearchModal";
import { AcknowledgementDebtForm, ReceivableItem, BaseCalculation, InstallmentItem, EMPTY_COLLATERALS_WITH_RECEIVABLES } from './types/operations';
import { PersonSearchResult, ProvinceResponse, CityResponse, JsonUploadData } from './types/api-responses';
import DisbursementManager from './components/form/DisbursementManager';
import FeesManager from './components/form/FeesManager';
import InstallmentManager from './components/form/InstallmentManager';
import GuarantorsManager from './components/form/GuarantorsManager';
import BusinessPartnerManager, { BusinessPartnerData } from './components/form/BusinessPartnerManager';
import CreditorSelector from './components/form/CreditorSelector';
import { CreditorPayload } from '../../../services/contractsManagementApi';
import CollateralsTabManager from './components/collaterals/CollateralsTabManager';
import ReceivablesManager from './components/receivables/ReceivablesManager';
import ReimportReceivablesButton from './components/receivables/ReimportReceivablesButton';
import ConvertReceivablesToInstallment from './components/form/ConvertReceivablesToInstallment';
import { useFileDownload } from './components/files/useFileDownload';
import appConfig from '../../../config/app-config';
import { ACKNOWLEDGEMENT_DEBT_CREDIT_OPERATION_TYPE_OPTIONS } from './constants/creditOperationTypes';
import { RECEIVING_ACCOUNT_OPTIONS } from './constants/receivingAccountOptions';
import { generateParticipants } from './utils/payload/participantUtils';
import { useContractActions } from './hooks/contract/useContractActions';
import BaseCalculationSection from './components/form/BaseCalculationSection';
import BillingConfigSection, { EMPTY_BILLING_CONFIG } from './components/form/BillingConfigSection';
import AmendmentReasonSection from './components/form/AmendmentReasonSection';
import AssignorBillingAddress, { EMPTY_ASSIGNOR_BILLING_ADDRESS } from './components/form/AssignorBillingAddress';
import BillingStatusBadge from './components/status/BillingStatusBadge';
import { useBillingStatus } from './hooks/data/useBillingStatus';
import ContractActionDropdown from './components/status/ContractActionDropdown';


const { address: CONTRACTS_API_URL, key: CONTRACTS_API_KEY } = appConfig.apis.contractsManagementApiEnv;
const { address: PERSON_SERVICE_URL, key: API_KEY_PERSON } = appConfig.apis.personApiEnv;

const OPERATIONS_LIST_URL = '/contracts/0/100/id/ASC/s=;';

const recalcFromInstallments = (installments: InstallmentItem[]): Partial<AcknowledgementDebtForm> => {
    const totalAmount = installments.reduce(
        (acc, inst) => acc + (Number.isFinite(inst.value) ? inst.value : 0),
        0
    );

    const validDueDates = installments
        .map(inst => inst.dueDate?.trim())
        .filter((d): d is string => !!d && isValidDate(d));

    const expirationDate = validDueDates.length > 0
        ? validDueDates.reduce((max, cur) => {
            const [dMax, mMax, yMax] = max.split('/').map(Number);
            const [dCur, mCur, yCur] = cur.split('/').map(Number);
            const tMax = new Date(yMax, mMax - 1, dMax).getTime();
            const tCur = new Date(yCur, mCur - 1, dCur).getTime();
            return tCur > tMax ? cur : max;
        }, validDueDates[0])
        : '';

    return {
        total_amount: totalAmount,
        expiration_date: expirationDate,
    };
};

const AcknowledgementDebtPage: React.FC = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const operationType = searchParams.get('operation_type') || 'ACKNOWLEDGEMENT_OF_DEBT';
    const credit_operation_id = params.credit_operation_id || params.id;
    const { handleDownload } = useFileDownload();
    const artifactFiles = useSignal<File[]>([]);
    const contractFiles = useSignal<File[]>([]);
    const [existingContractFile, setExistingContractFile] = useState<string>('');
    const [existingArtifactFiles, setExistingArtifactFiles] = useState<string[]>([]);
    const [removedContractFile, setRemovedContractFile] = useState<boolean>(false);
    const [removedArtifactFiles, setRemovedArtifactFiles] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 25;
    const [hasInvalidDueDates, setHasInvalidDueDates] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [businessPartner, setBusinessPartner] = useState<BusinessPartnerData | null>(null);
    const [creditor, setCreditor] = useState<CreditorPayload | null>(null);
    const { fetchProvinces } = useProvinces();
    const { fetchCities } = useCities();
    const [provinces, setProvinces] = useState<Array<{ value: string, label: string, initials: string }>>([]);
    const [cities, setCities] = useState<Array<{ value: string, label: string }>>([]);
    const [lastInstallmentDueDate, setLastInstallmentDueDate] = useState<string | undefined>(undefined);
    const apiVersion = useSignal('');

    const form = useSignal<AcknowledgementDebtForm>({
        assignor: '',
        recipient: '',
        total_amount: 0,
        loan_amount: 0,
        expiration_date: '',
        emission_date: '',
        approved_on: '',
        operation_number: '',
        external_link: '',
        rebate_amount: 0,
        guarantee_value: null,
        status: '',
        sequencial_number: 0,
        originator: '',
        operation_uf: '',
        operation_city: '',
        original_contract_discharged: [],
        credit_product_type_name: '',
        receiving_account_name: '',
        crop_type: {
            name: '',
            id: ''
        },
        collaterals: EMPTY_COLLATERALS_WITH_RECEIVABLES,
        disbursement: {
            totalAmount: 0,
            disbursements: []
        },
        installments: [],
        installment_format: 'VALOR',
        guarantors: {
            items: [],
            quantity: 0
        },
        main_risk: {
            uuid: '',
            name: ''
        },
        fees: {
            items: [],
            quantity: 0
        },
        artifacts: [],
        signed_contract_file: {
            file_id: '',
            file_name: ''
        },
        base_calculation: {
            tir_annual: null,
            tir_monthly: null,
            spread_annual: null,
            vpl: null,
            vpl_margin: null,
            duration_days: null
        } as BaseCalculation,
        billing_config: EMPTY_BILLING_CONFIG
    });

    const isLoading = useSignal(false);
    const errorFields = useSignal<ErrorField[]>([]);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
    const [isAmendmentModalOpen, setIsAmendmentModalOpen] = useState(false);
    const [amendmentReason, setAmendmentReason] = useState<string>('');
    const [amendmentNumber, setAmendmentNumber] = useState<number>(0);
    const [isContractLoaded, setIsContractLoaded] = useState(false);
    const [billingStatus, setBillingStatus] = useState<{ billed?: boolean; billing_info?: string }>({});
    const { billingData, isPolling, startPolling, fetchBillingStatus } = useBillingStatus(credit_operation_id);

    const updateForm = (updater: Partial<AcknowledgementDebtForm>) => {
        form.value = { ...form.value, ...updater };
        setHasUnsavedChanges(true);
    };

    const [selectedDestinatario, setSelectedDestinatario] = useState('');

    const validateAllFields = useCallback((): boolean => {
        const f = form.value;
        const rules: Array<{ condition: boolean; label: string; fieldName: string }> = [
            { condition: !creditor, label: 'Credor', fieldName: 'creditor' },
            { condition: !f.original_contract_discharged || f.original_contract_discharged.length === 0, label: 'Contratos Originais Quitados', fieldName: 'original_contract_discharged' },
            { condition: !f.external_link?.trim(), label: 'Card JIRA', fieldName: 'external_link' },
            { condition: !f.credit_product_type_name?.trim(), label: 'Tipo de Operação de Crédito', fieldName: 'credit_product_type_name' },
            { condition: !f.receiving_account_name?.trim(), label: 'Conta para Recebimento', fieldName: 'receiving_account_name' },
            { condition: !f.operation_uf?.trim(), label: 'UF da Operação', fieldName: 'operation_uf' },
            { condition: !f.operation_city?.trim(), label: 'Cidade da Operação', fieldName: 'operation_city' },
            { condition: !f.emission_date?.trim(), label: 'Data de Emissão', fieldName: 'emission_date' },
            { condition: !f.approved_on?.trim(), label: 'Data de Aprovação', fieldName: 'approved_on' },
            { condition: !selectedDestinatario || selectedDestinatario === '00000000-0000-0000-0000-000000000000', label: 'Beneficiário', fieldName: 'recipient' },
            { condition: !f.loan_amount, label: 'Valor Financiado', fieldName: 'loan_amount' },
            { condition: !f.total_amount, label: 'Valor Nominal', fieldName: 'nominal_value' },
            { condition: !f.disbursement.totalAmount || f.disbursement.totalAmount <= 0, label: 'Desembolsos', fieldName: 'disbursement' },
            { condition: f.billing_config?.generate_billets === null, label: 'Gerar boletos para cobrança', fieldName: 'generate_billets' },
            { condition: f.billing_config?.automatic_preventive_collection === null, label: 'Cobrança preventiva automática', fieldName: 'automatic_preventive_collection' },
            { condition: f.billing_config?.automatic_overdue_collection === null, label: 'Cobrança de vencidos automática', fieldName: 'automatic_overdue_collection' },
            { condition: f.collaterals?.assets?.receivables?.overcollateral === null || f.collaterals?.assets?.receivables?.overcollateral === undefined, label: 'Operação com overcollateral', fieldName: 'overcollateral' },
        ];

        const failed = rules.filter(r => r.condition);
        errorFields.value = failed.map(r => ({ fieldName: r.fieldName, message: 'Campo obrigatório' }));

        if (failed.length > 0) {
            toast.error(`Campos obrigatórios não preenchidos: ${failed.map(r => r.label).join(', ')}`);
            return false;
        }

        const receivableItems = f.collaterals?.assets?.receivables?.items || [];
        if (f.installments && f.installments.length > 0 && receivableItems.length > 0) {
            const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
            const invalid = receivableItems.filter(item =>
                !item.operation_date || item.operation_date.trim() === '' || item.operation_date.trim() === '-' || !dateRegex.test(item.operation_date.trim()) ||
                !item.present_value || item.present_value <= 0
            );
            if (invalid.length > 0) {
                toast.error(`${invalid.length} recebível(is) com "Data Operação Agrolend" ou "Valor Presente" inválidos. Processe a planilha antes de finalizar.`);
                return false;
            }
        }
        return true;
    }, [form, selectedDestinatario, errorFields, creditor]);

    const { handleCancelContract, handleFinishContract, handleBillingProcess } = useContractActions({
        apiUrl: CONTRACTS_API_URL || '', apiKey: CONTRACTS_API_KEY || '',
        creditOperationId: credit_operation_id,
        setLoading: (v: boolean) => { isLoading.value = v; },
        hasUnsavedChanges,
        hasInvalidDueDates,
        validateAllFields,
        setIsCancelModalOpen,
        setIsFinishModalOpen,
        onRefresh: () => { fetchContractData(); startPolling(); },
        contractStatus: form.value.status,
        billingAddress: form.value.assignor_billing_address,
        requiresBillingAddress: true,
    });

    const [recipientData, setRecipientData] = useState<PersonSearchResult | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedReceivable, setSelectedReceivable] = useState<any>(null);
    const [isReceivableModalOpen, setIsReceivableModalOpen] = useState(false);
    const { products, setProducts, fetchPersons, fetchProducts } = useOperationData({
        personServiceUrl: PERSON_SERVICE_URL,
        personApiKey: API_KEY_PERSON,
        contractsApiUrl: CONTRACTS_API_URL,
        contractsApiKey: CONTRACTS_API_KEY
    });
    const [assignorParticipant, setAssignorParticipant] = useState<{ uuid: string; name: string; document: string; id: number | null }>({ uuid: '', name: '', document: '', id: null });
    const [recipientParticipant, setRecipientParticipant] = useState<{ uuid: string; name: string; document: string; id: number | null }>({ uuid: '', name: '', document: '', id: null });
    const productManagement = useProductManagement({
        apiUrl: CONTRACTS_API_URL,
        apiKey: CONTRACTS_API_KEY,
        setProducts,
        onProductCreated: (product) => {
            form.value = { ...form.value, crop_type: { id: product.uuid, name: product.name } };
        }
    });

    const { submitContract } = useContractSubmit({
        apiUrl: CONTRACTS_API_URL,
        apiKey: CONTRACTS_API_KEY,
        endpoint: '/contracts/receivables_assignment/update',
        setLoading: (v: boolean) => { isLoading.value = v; }
    });

    const handleSelectPerson = (selected: PersonSearchResult) => {
        const cnpjDoc = selected.documents?.find((doc) => doc.type === 'CNPJ');
        const cpfDoc = selected.documents?.find((doc) => doc.type === 'CPF');
        const document = cnpjDoc?.number || cpfDoc?.number || '';

        if (!document || document.trim() === '') {
            toast.error("Beneficiário sem documento cadastrado");
            return;
        }

        setSelectedDestinatario(selected.uuid);
        form.value = { ...form.value, recipient: selected.name || '' };

        setRecipientData(selected);
    };

    const { fetchContract } = useContractFetch({
        apiUrl: CONTRACTS_API_URL || '',
        apiKey: CONTRACTS_API_KEY || '',
        creditOperationId: credit_operation_id,
        setLoading: (v: boolean) => { isLoading.value = v; },
        setHasUnsavedChanges,
        setIsContractLoaded,
    });

    const fetchContractData = async () => {
        await fetchContract({
            onMetadata: (metadata) => {
                if (metadata.apiVersion) apiVersion.value = metadata.apiVersion;
                if (metadata.amendmentReason) setAmendmentReason(metadata.amendmentReason);
                if (metadata.amendmentNumber) setAmendmentNumber(metadata.amendmentNumber);
                setBillingStatus(metadata.billingStatus);
            },
            onBusinessPartner: setBusinessPartner,
            onCreditor: setCreditor,
            onFiles: (files) => {
                setExistingContractFile(files.contractFile);
                setExistingArtifactFiles(files.artifactFiles);
            },
            onData: ({ data }) => {
                const mapped = mapAcknowledgementDebtApiResponseToForm(data, form.value);

                if (mapped.assignorPart) {
                    setAssignorParticipant({
                        uuid: mapped.assignorPart.person_id || '00000000-0000-0000-0000-000000000000',
                        name: mapped.assignorPart.name || '',
                        document: mapped.assignorPart.document || '',
                        id: mapped.assignorPart.id ? Number(mapped.assignorPart.id) : null
                    });
                }

                if (mapped.recipientParticipant) {
                    setSelectedDestinatario(mapped.recipientParticipant.person_id || '00000000-0000-0000-0000-000000000000');
                    setRecipientParticipant({
                        uuid: mapped.recipientParticipant.person_id || '00000000-0000-0000-0000-000000000000',
                        name: mapped.recipientParticipant.name || '',
                        document: mapped.recipientParticipant.document || '',
                        id: mapped.recipientParticipant.id ? Number(mapped.recipientParticipant.id) : null
                    });
                }

                form.value = mapped.form;

                if (mapped.form.status === 'Gerado' && mapped.form.installments?.length > 0) {
                    const updates = recalcFromInstallments(mapped.form.installments);
                    form.value = { ...form.value, ...updates };
                }
            },
        });
    };

    useEffect(() => {
        const fetchProvincesData = async () => {
            try {
                const provincesData: ProvinceResponse[] = await fetchProvinces();
                const provinceItems = provincesData.map((item: ProvinceResponse) => ({
                    value: item.initials,
                    label: item.name,
                    initials: item.initials
                }));
                setProvinces(provinceItems);
                return provinceItems;
            } catch (error: unknown) {
                toast.error('Erro ao carregar províncias');
                return [];
            }
        };

        const loadDataSequentially = async () => {
            const provincesData = await fetchProvincesData();

            if (credit_operation_id) {
                await fetchContractData();
                if (form.value.operation_uf) {
                    const selectedProvince = provincesData.find((p: { value: string; label: string; initials: string }) => p.value === form.value.operation_uf);

                    if (selectedProvince) {
                        fetchCitiesWrapper(selectedProvince.initials);
                    }
                }
            }
        };

        fetchPersons();
        fetchProducts();
        loadDataSequentially();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [credit_operation_id]);

    const handleProvinceChange = async (initials: string) => {
        fetchCitiesWrapper(initials);
        if (initials) {
            provinces.find(p => p.initials === initials);
            form.value = {
                ...form.value,
                operation_uf: initials,
                operation_city: ''
            };
        } else {
            setCities([]);
            form.value = {
                ...form.value,
                operation_uf: '',
                operation_city: ''
            };
        }
    };

    const fetchCitiesWrapper = async (initials: string) => {
        try {
            if (initials) {

                const citiesData: CityResponse[] = await fetchCities(initials);
                const cityItems = citiesData.map((item: CityResponse) => ({
                    value: item.name,
                    label: item.name
                }));
                setCities(cityItems);
            }
        } catch (error: unknown) {
            void error;
        }
    };





    const handleJsonDataLoaded = (data: JsonUploadData) => {
        const receivablesData = data.duplicates || data.receivables;
        if (receivablesData?.items) {
            const mappedItems: ReceivableItem[] = receivablesData.items.map(mapReceivableFromApi);

            form.value = {
                ...form.value,
                operation_number: data.operation_code || form.value.operation_number,
                collaterals: {
                    ...form.value.collaterals,
                    assets: {
                        ...form.value.collaterals.assets,
                        receivables: {
                            ...form.value.collaterals.assets.receivables,
                            quantity: receivablesData.quantity || receivablesData.items.length,
                            total: receivablesData.total || 0,
                            items: mappedItems,
                            operation_id: data.operation_id ?? receivablesData?.operation_id ?? form.value.collaterals.assets.receivables?.operation_id,
                        }
                    }
                }
            };
        }
    };

    useReceivablesDownloader(
        form.value.sequencial_number || 0,
        form.value.collaterals.assets.receivables?.items?.filter(item =>
            item.document_id && item.document_id.trim() !== '' && item.receivable_amount > 0
        ).length || 0,
        handleJsonDataLoaded,
        amendmentNumber,
        isContractLoaded,
        form.value.credit_product_type_name === 'CONTRACT_ASSIGNMENT'
    );

    const isFormDisabled = isContractDisabled(form.value.status);

    const buildParticipants = () => {
        const recipientDocument = recipientData?.documents?.find((doc) => doc.type === 'CNPJ')?.number ||
            recipientData?.documents?.find((doc) => doc.type === 'CPF')?.number ||
            recipientParticipant.document || '';

        return generateParticipants({
            assignorName: form.value.assignor,
            assignorParticipant,
            recipientName: form.value.recipient,
            recipientParticipant,
            recipientDocument,
            selectedBeneficiary: selectedDestinatario,
            guarantors: form.value.guarantors.items,
            businessPartner,
            conditionalPersonId: true,
        });
    };

    const validateBeforeSubmit = (): boolean => {
        if (isFormDisabled) {
            toast.error('Não é possível salvar. Status não permite alterações.');
            return false;
        }
        if (!CONTRACTS_API_URL || !CONTRACTS_API_KEY) {
            toast.error('Configuração da API não encontrada');
            return false;
        }
        if (!creditor) {
            toast.error('Selecione um credor');
            return false;
        }
        if (!form.value.original_contract_discharged || form.value.original_contract_discharged.length === 0) {
            toast.error('Informe pelo menos uma operação quitada');
            return false;
        }
        if (!selectedDestinatario || selectedDestinatario === '00000000-0000-0000-0000-000000000000') {
            toast.error('Selecione um beneficiário');
            return false;
        }
        if (!form.value.external_link?.trim()) {
            toast.error('Card JIRA é obrigatório');
            return false;
        }
        if (!form.value.receiving_account_name?.trim()) {
            toast.error('Conta para Recebimento é obrigatória');
            return false;
        }
        if (!form.value.disbursement.totalAmount || form.value.disbursement.totalAmount <= 0) {
            toast.error('Valor Nominal é obrigatório');
            return false;
        }
        const sumDisbursements = form.value.disbursement.disbursements.reduce((sum, d) => sum + d.amount, 0);
        if (sumDisbursements > form.value.disbursement.totalAmount) {
            toast.error(`Soma dos desembolsos (R$ ${sumDisbursements.toFixed(2)}) não pode exceder o Valor Nominal (R$ ${form.value.disbursement.totalAmount.toFixed(2)})`);
            return false;
        }
        if (form.value.collaterals.assets.receivables?.items?.some(item => !item.receivable_due_date || item.receivable_due_date.trim() === '')) {
            toast.error('Existem recebíveis sem data de vencimento. Corrija antes de salvar.');
            return false;
        }
        const receivables = form.value.collaterals.assets.receivables;
        if (receivables?.spreadsheet_processed && receivables?.spreadsheet_items_count && receivables.spreadsheet_items_count > 0) {
            const receivablesCount = receivables.items?.length || 0;
            if (receivablesCount !== receivables.spreadsheet_items_count) {
                toast.error(`Planilha processada: ${receivables.spreadsheet_items_count} itens na planilha, mas ${receivablesCount} recebíveis na lista. Todos os itens devem estar presentes.`);
                return false;
            }
            if (receivables.items?.some(item => item.has_value_divergence)) {
                toast.error('Planilha processada com divergências de valores. Corrija as divergências antes de salvar.');
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateBeforeSubmit()) return;


        try {
            isLoading.value = true;

            await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            const dueDateValue = lastInstallmentDueDate || form.value.expiration_date || null;

            const payload = buildAcknowledgementDebtPayload({
                form: form.value,
                operationType: operationType || "ACKNOWLEDGEMENT_OF_DEBT",
                creditOperationId: credit_operation_id,
                selectedDestinatario,
                lastInstallmentDueDate: dueDateValue || undefined,
                participants: buildParticipants(),
                creditor,
                artifactFiles: artifactFiles.value,
                contractFiles: contractFiles.value,
                existingArtifactFiles,
                removedArtifactFiles,
                existingContractFile,
                removedContractFile,
            });

            await submitContract(
                payload,
                { contractFiles: contractFiles.value, artifactFiles: artifactFiles.value },
                {
                    successMessage: credit_operation_id
                        ? "Confissão de dívida atualizado com sucesso!"
                        : "Confissão de dívida criado com sucesso!",
                    onSuccess: async () => {
                        setHasUnsavedChanges(false);
                        if (credit_operation_id) {
                            await fetchContractData();
                            contractFiles.value = [];
                            artifactFiles.value = [];
                            setRemovedContractFile(false);
                            setRemovedArtifactFiles([]);
                        } else {
                            navigate(-1);
                        }
                    },
                    onError: (errors) => {
                        errorFields.value = errors;
                    }
                }
            );
        } finally {
            isLoading.value = false;
        }
    };

    return (
        <Fragment>
            <PageForm
                pageTitle="Confissão de Dívida"
                apiVersion={apiVersion.value}
                isLoading={isLoading.value}
                breadcrumb={[
                    { label: 'Contratos', url: OPERATIONS_LIST_URL },
                    { label: 'Confissão de Dívida', url: '#' }
                ]}
                controlSlot={
                    <div className="pt-4 flex justify-end">
                        <button type="button"
                            className="btn-cancel"
                            onClick={() => navigate(-1)}
                            disabled={isLoading.value}
                        >
                            Voltar
                        </button>

                        <ContractActionDropdown
                            onSave={handleSubmit}
                            onCancel={() => setIsCancelModalOpen(true)}
                            onFinish={() => setIsFinishModalOpen(true)}
                            onBilling={handleBillingProcess}
                            onAmendment={() => setIsAmendmentModalOpen(true)}
                            creditOperationId={credit_operation_id}
                            apiUrl={CONTRACTS_API_URL || ''}
                            apiKey={CONTRACTS_API_KEY || ''}
                            setLoading={(v: boolean) => { isLoading.value = v; }}
                            onRegistered={() => { fetchContractData(); }}
                            billingAddress={form.value.assignor_billing_address}
                            requiresBillingAddress={true}
                            isLoading={isLoading.value}
                            isFormDisabled={isFormDisabled}
                            hasContractId={!!credit_operation_id}
                            contractStatus={form.value.status || ''}
                            isBilled={!!billingStatus.billed}
                        />
                    </div>
                }
            >
                <div className="container mx-auto px-2 py-4">
                    <CreditorSelector
                        value={creditor}
                        onChange={(c) => {
                            setCreditor(c);
                            setHasUnsavedChanges(true);
                        }}
                        disabled={isLoading.value}
                        readOnly={isFormDisabled}
                    />

                    <h3 className="text-md font-semibold mt-4 mb-3 text-gray-700 dark:text-gray-200">Informações Gerais</h3>
                    <div className="mb-4">
                        <BadgeStatus
                            color={getStatusColor(form.value.status)}
                            text={form.value.status || ''}
                        />
                    </div>
                    {amendmentNumber > 0 && <AmendmentReasonSection reason={amendmentReason} />}
                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-2">
                            <TextEdit
                                id="sequencial_number"
                                label="Número do Contrato"
                                value={amendmentNumber > 0 ? `${form.value.sequencial_number}-${amendmentNumber}` : form.value.sequencial_number?.toString() || ''}
                                disabled={true}
                            />
                        </div>
                    </div>

                    <h3 className="text-md font-semibold mt-4 mb-3 text-gray-700 dark:text-gray-200">Dados Gerais</h3>
                    <div className="mb-6">
                        <label htmlFor="discharged-contract-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Operações Quitadas <span className="text-red-600">*</span>
                        </label>
                        <div className="flex items-center gap-2">
                            <input
                                id="discharged-contract-input"
                                type="number"
                                className="w-48 h-[42px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                placeholder="Digite o número"
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        const value = Number.parseInt((e.target as HTMLInputElement).value);
                                        if (value > 0 && !form.value.original_contract_discharged.includes(value)) {
                                            updateForm({ original_contract_discharged: [...form.value.original_contract_discharged, value] });
                                            (e.target as HTMLInputElement).value = '';
                                        }
                                    }
                                }}
                            />
                            <button type="button"
                                onClick={(e) => {
                                    const input = (e.target as HTMLButtonElement).parentElement?.querySelector('input') as HTMLInputElement;
                                    const value = Number.parseInt(input.value);
                                    if (value > 0 && !form.value.original_contract_discharged.includes(value)) {
                                        updateForm({ original_contract_discharged: [...form.value.original_contract_discharged, value] });
                                        input.value = '';
                                    }
                                }}
                                className="h-[42px] px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Adicionar
                            </button>
                            <div className="flex flex-wrap gap-2 flex-1">
                                {form.value.original_contract_discharged.map((id, index) => (
                                    <span key={id} className="inline-flex items-center gap-1 h-[42px] px-3 py-2 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 rounded">
                                        {id}
                                        <button type="button"
                                            onClick={() => updateForm({ original_contract_discharged: form.value.original_contract_discharged.filter((_, i) => i !== index) })}
                                            className="text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                        {form.value.original_contract_discharged.length === 0 && (
                            <p className="text-sm text-red-600 dark:text-red-400 mt-2">Adicione pelo menos uma operação quitada</p>
                        )}
                    </div>

                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-3">
                            <TextEdit
                                id="operation_number"
                                label="Número da Operação"
                                value={form.value.operation_number}
                                onChange={(e) => updateForm({ operation_number: e.target.value })}
                                errorMessage={getErrorMessage(errorFields.value, "operation_number")}
                            />
                        </div>
                        <div className="col-span-2">
                            <TextEdit
                                id="external_link"
                                label="Card JIRA"
                                value={form.value.external_link}
                                onChange={(e) => updateForm({ external_link: e.target.value })}
                                errorMessage={getErrorMessage(errorFields.value, "external_link")}
                            />
                        </div>
                        <div className="col-span-3">
                            <SelectTextField
                                id="credit_product_type_name"
                                label="Tipo de Operação de Crédito"
                                value={form.value.credit_product_type_name}
                                onChange={(e) => updateForm({ credit_product_type_name: e.target.value })}
                                items={ACKNOWLEDGEMENT_DEBT_CREDIT_OPERATION_TYPE_OPTIONS}
                                errorMessage={getErrorMessage(errorFields.value, "credit_product_type_name")}
                            />
                        </div>
                        <div className="col-span-3">
                            <SelectTextField
                                id="receiving_account_name"
                                label="Conta para Recebimento"
                                value={form.value.receiving_account_name || ''}
                                onChange={(e) => updateForm({ receiving_account_name: e.target.value })}
                                items={RECEIVING_ACCOUNT_OPTIONS}
                                errorMessage={getErrorMessage(errorFields.value, "receiving_account_name")}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-3">
                            <TextEdit
                                id="originador"
                                label="Originador"
                                value={form.value.originator}
                                onChange={(e) => updateForm({ originator: e.target.value })}
                                errorMessage={getErrorMessage(errorFields.value, "originator")}
                            />
                        </div>
                        <div className="col-span-2">
                            <SelectTextField
                                id="operation_uf"
                                label="UF da Operação"
                                value={form.value.operation_uf.toString()}
                                items={provinces}
                                onChange={(e) => handleProvinceChange(e.target.value)}
                                errorMessage={getErrorMessage(errorFields.value, "operation_uf")}
                            />
                        </div>
                        <div className="col-span-3">
                            <SelectTextField
                                id="operation_city"
                                label="Cidade da Operação"
                                value={form.value.operation_city}
                                items={cities}
                                onChange={(e) => updateForm({ operation_city: e.target.value })}
                                errorMessage={getErrorMessage(errorFields.value, "operation_city")}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-4">
                            <TextEdit
                                id="assignor"
                                label="Devedor"
                                value={form.value.assignor}
                                onChange={(e) => updateForm({ assignor: e.target.value })}
                                errorMessage={getErrorMessage(errorFields.value, "assignor")}
                                disabled={true}
                            />
                        </div>
                        <div className="col-span-4">
                            <TextEdit
                                id="emission_date"
                                label="Data de Emissão"
                                value={form.value.emission_date}
                                onChange={(e) => updateForm({ emission_date: applyDateMask(e.target.value) })}
                                onBlur={(e) => {
                                    if (!isValidDate(e.target.value)) {
                                        toast.error('Data de Emissão inválida');
                                        e.target.focus();
                                    }
                                }}
                                errorMessage={getErrorMessage(errorFields.value, "emission_date")}
                            />
                        </div>
                        <div className="col-span-4">
                            <TextEdit
                                id="approved_on"
                                label="Data de Aprovação"
                                value={form.value.approved_on}
                                onChange={(e) => updateForm({ approved_on: applyDateMask(e.target.value) })}
                                onBlur={(e) => {
                                    if (e.target.value && !isValidDate(e.target.value)) {
                                        toast.error('Data de Aprovação inválida');
                                        e.target.focus();
                                    }
                                }}
                                errorMessage={getErrorMessage(errorFields.value, "approved_on")}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 mb-6">
                        <div className="col-span-6">
                            <TextEdit
                                id="recipient"
                                label="Beneficiário"
                                value={form.value.recipient}
                                disabled={true}
                            />
                        </div>
                        <div className="col-span-1 flex items-end">
                            <button type="button"
                                onClick={() => setIsModalOpen(true)}
                                className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 mb-4 h-10 w-[75px] flex items-center justify-center"
                                title="Buscar beneficiário"
                            >
                                <FaSearch />
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 mb-6">
                        <div className="col-span-6">
                            <SelectTextField
                                id="product_id"
                                label="Produto"
                                value={form.value.crop_type.id}
                                items={products}
                                onChange={(e) => {
                                    const selected = products.find(product => product.value === e.target.value);
                                    form.value = {
                                        ...form.value,
                                        crop_type: {
                                            id: e.target.value,
                                            name: selected?.label || ''
                                        }
                                    };
                                    setHasUnsavedChanges(true);
                                }}
                                errorMessage={getErrorMessage(errorFields.value, "product")}
                            />
                        </div>
                        <div className="col-span-1 flex items-end">
                            <button type="button"
                                onClick={() => productManagement.setIsProductModalOpen(true)}
                                className="btn-add mb-4 h-10 w-[75px]"
                                title="Novo produto"
                            >
                                Novo
                            </button>
                        </div>
                    </div>

                    <h3 className="text-md font-semibold mt-4 mb-3 text-gray-700 dark:text-gray-200">Valor Financiado</h3>
                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-3">
                            <CurrencyInput
                                id="loan_amount"
                                label="Valor Financiado"
                                prefix="R$"
                                fractionDigits={2}
                                value={form.value.loan_amount}
                                onChange={(value) => {
                                    updateForm({ loan_amount: value });
                                }}
                            />
                        </div>
                    </div>

                    <BaseCalculationSection
                        value={form.value.base_calculation}
                        onChange={(updated) => updateForm({ base_calculation: updated })}
                    />

                    <BillingConfigSection
                        value={form.value.billing_config || EMPTY_BILLING_CONFIG}
                        onChange={(updated) => updateForm({ billing_config: updated })}
                        disabled={isFormDisabled}
                        errorFields={errorFields.value}
                    />

                    <AssignorBillingAddress
                        value={form.value.assignor_billing_address || EMPTY_ASSIGNOR_BILLING_ADDRESS}
                        onChange={(updated) => { form.value = { ...form.value, assignor_billing_address: updated }; }}
                        disabled={isFormDisabled}
                        assignorPersonId={assignorParticipant.uuid}
                        assignorName={assignorParticipant.name}
                        assignorDocument={assignorParticipant.document}
                        contractStatus={form.value.status}
                    />

                    <BillingStatusBadge billed={billingStatus.billed} billingInfo={billingStatus.billing_info} billingData={billingData} isPolling={isPolling} fetchBillingStatus={fetchBillingStatus} />

                    <DisbursementManager
                        disbursements={form.value.disbursement.disbursements}
                        totalAmount={form.value.disbursement.totalAmount}
                        onTotalAmountChange={(value) => {
                            const sum = form.value.disbursement.disbursements.reduce((s, d) => s + d.amount, 0);
                            form.value = {
                                ...form.value,
                                total_amount: value,
                                rebate_amount: value - sum,
                                disbursement: { ...form.value.disbursement, totalAmount: value }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onAdd={(disbursement) => {
                            const currentDisbursements = [...form.value.disbursement.disbursements, disbursement];
                            const sum = currentDisbursements.reduce((s, d) => s + d.amount, 0);
                            form.value = {
                                ...form.value,
                                rebate_amount: form.value.disbursement.totalAmount - sum,
                                disbursement: { ...form.value.disbursement, disbursements: currentDisbursements }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onUpdate={(index, disbursement) => {
                            const disbursements = form.value.disbursement.disbursements.map((d, i) => i === index ? disbursement : d);
                            const sum = disbursements.reduce((s, d) => s + d.amount, 0);
                            form.value = {
                                ...form.value,
                                rebate_amount: form.value.disbursement.totalAmount - sum,
                                disbursement: { ...form.value.disbursement, disbursements }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onRemove={(index) => {
                            const disbursements = form.value.disbursement.disbursements.filter((_, i) => i !== index);
                            const sum = disbursements.reduce((s, d) => s + d.amount, 0);
                            form.value = {
                                ...form.value,
                                rebate_amount: form.value.disbursement.totalAmount - sum,
                                disbursement: { ...form.value.disbursement, disbursements }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        disabled={isLoading.value}
                    />

                    <div className="grid grid-cols-12 gap-4 mt-4">
                        <div className="col-span-2">
                            <CurrencyInput
                                id="totalAmount"
                                label="Total dos Desembolsos"
                                prefix="R$"
                                fractionDigits={2}
                                value={form.value.disbursement.totalAmount - (form.value.rebate_amount || 0)}
                                disabled={true}
                            />
                        </div>
                        <div className="col-span-2">
                            <CurrencyInput
                                id="rebate_amount"
                                label="Valor de Abatimento"
                                prefix="R$"
                                fractionDigits={2}
                                value={form.value.rebate_amount}
                                onChange={(value) => updateForm({ rebate_amount: value })}
                                errorMessage={getErrorMessage(errorFields.value, "rebate_amount")}
                            />
                        </div>
                    </div>

                    <FeesManager
                        fees={form.value.fees}
                        onAdd={(fee) => {
                            const newItems = [...form.value.fees.items, fee];
                            form.value = {
                                ...form.value,
                                fees: {
                                    items: newItems,
                                    quantity: newItems.length
                                }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onUpdate={(index, fee) => {
                            const items = form.value.fees.items.map((f, i) => i === index ? fee : f);
                            form.value = {
                                ...form.value,
                                fees: {
                                    items,
                                    quantity: items.length
                                }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onRemove={(index) => {
                            const newItems = form.value.fees.items.filter((_, i) => i !== index);
                            form.value = {
                                ...form.value,
                                fees: {
                                    items: newItems,
                                    quantity: newItems.length
                                }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        disabled={isLoading.value}
                    />

                    <h3 className="text-md font-semibold mt-4 mb-3 text-gray-700 dark:text-gray-200">Vencimento</h3>
                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-3">
                            <CurrencyInput
                                id="total_amount"
                                label="Valor Nominal *"
                                prefix="R$"
                                fractionDigits={2}
                                value={form.value.total_amount}
                                onChange={(value) => updateForm({ total_amount: value })}
                                errorMessage={getErrorMessage(errorFields.value, "total_amount")}
                                disabled={false}
                            />
                        </div>
                        <div className="col-span-3">
                            <TextEdit
                                id="expiration_date"
                                label="Data de vencimento"
                                value={form.value.expiration_date}
                                onChange={(e) => updateForm({ expiration_date: applyDateMask(e.target.value) })}
                                onBlur={(e) => {
                                    if (!isValidDate(e.target.value)) {
                                        toast.error('Data de vencimento inválida');
                                        e.target.focus();
                                    }
                                }}
                                errorMessage={getErrorMessage(errorFields.value, "expiration_date")}
                                disabled={!form.value.installments.some(inst => inst.format === 'PERCENTUAL')}
                            />
                        </div>
                    </div>

                    <InstallmentManager
                        installments={form.value.installments}
                        onAdd={(installment) => {
                            const newInstallments = [...form.value.installments, installment];
                            const updates = recalcFromInstallments(newInstallments);
                            form.value = {
                                ...form.value,
                                installments: newInstallments,
                                ...updates
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onUpdateItem={(index, installment) => {
                            const installments = form.value.installments.map((inst, i) => i === index ? installment : inst);
                            const updates = recalcFromInstallments(installments);
                            form.value = {
                                ...form.value,
                                installments,
                                ...updates
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onRemove={(index) => {
                            const installments = form.value.installments.filter((_, i) => i !== index);
                            const updates = recalcFromInstallments(installments);
                            form.value = {
                                ...form.value,
                                installments,
                                ...updates
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onUpdate={(updatedInstallments, lastDueDate) => {
                            setLastInstallmentDueDate(lastDueDate);
                            const updates = recalcFromInstallments(updatedInstallments);
                            form.value = {
                                ...form.value,
                                installments: updatedInstallments,
                                ...updates
                            };
                            setHasUnsavedChanges(true);
                        }}
                        disabled={isLoading.value}
                    />

                    <GuarantorsManager
                        guarantors={form.value.guarantors}
                        guaranteeValue={form.value.guarantee_value}
                        onGuaranteeValueChange={(value) => updateForm({ guarantee_value: value })}
                        onAdd={(guarantor) => {
                            const newItems = [...form.value.guarantors.items, guarantor];
                            form.value = {
                                ...form.value,
                                guarantors: {
                                    items: newItems,
                                    quantity: newItems.length
                                }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onRemove={(index) => {
                            const newItems = form.value.guarantors.items.filter((_, i) => i !== index);
                            form.value = {
                                ...form.value,
                                guarantors: {
                                    items: newItems,
                                    quantity: newItems.length
                                }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        disabled={isLoading.value}
                        mainRiskValue={form.value.main_risk.uuid}
                        mainRiskOptions={[
                            { value: assignorParticipant.uuid || '00000000-0000-0000-0000-000000000000', label: assignorParticipant.name || form.value.assignor || 'Devedor' },
                            ...form.value.guarantors.items.map(g => ({ value: g.uuid, label: g.name }))
                        ]}
                        onMainRiskChange={(value) => {
                            const selected = form.value.guarantors.items.find((g: any) => g.uuid === value);
                            form.value = {
                                ...form.value,
                                main_risk: {
                                    uuid: value,
                                    name: selected?.name || assignorParticipant.name || form.value.assignor || ''
                                }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        mainRiskError={getErrorMessage(errorFields.value, "main_risk")}
                    />

                    <BusinessPartnerManager
                        value={businessPartner}
                        onChange={(partner) => {
                            setBusinessPartner(partner);
                            setHasUnsavedChanges(true);
                        }}
                        disabled={isLoading.value}
                    />

                    <h3 className="text-md font-semibold mt-4 mb-3 text-gray-700 dark:text-gray-200">Contrato</h3>
                    <div>
                        <div
                            className="flex items-center justify-center w-full"
                            onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (e.dataTransfer.files?.[0]) {
                                    contractFiles.value = [e.dataTransfer.files[0]];
                                }
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                        >
                            <label htmlFor="contract-upload"
                                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                            >
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                    <svg className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2" />
                                    </svg>
                                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                        <span className="font-semibold">Upload do Contrato</span>
                                    </p>
                                </div>
                                <input
                                    id="contract-upload"
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            contractFiles.value = [file];
                                        }
                                    }}
                                />
                            </label>
                        </div>
                        {existingContractFile && !removedContractFile && (
                            <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-800 border border-blue-200 dark:border-blue-600 rounded flex justify-between items-center">
                                <p className="text-sm text-blue-700 dark:text-blue-100">
                                    <strong>Arquivo atual:</strong>{' '}
                                    <button type="button"
                                        onClick={() => handleDownload(credit_operation_id || '', existingContractFile)}
                                        className="underline hover:text-blue-900 dark:hover:text-blue-300 cursor-pointer bg-transparent border-0 p-0"
                                    >
                                        {existingContractFile}
                                    </button>
                                </p>
                                <button type="button"
                                    onClick={() => setRemovedContractFile(true)}
                                    className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                                >
                                    <FaTimes className="inline mr-1" /> Remover
                                </button>
                            </div>
                        )}
                        {removedContractFile && (
                            <div className="mt-2 p-2 bg-red-50 dark:bg-red-800 border border-red-200 dark:border-red-600 rounded flex justify-between items-center">
                                <p className="text-sm text-red-700 dark:text-red-100">
                                    <strong>Arquivo removido:</strong> {existingContractFile}
                                </p>
                                <button type="button"
                                    onClick={() => setRemovedContractFile(false)}
                                    className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600"
                                >
                                    Restaurar
                                </button>
                            </div>
                        )}
                        {contractFiles.value.length > 0 && (
                            <div className="mt-2 p-2 bg-green-50 dark:bg-green-800 border border-green-200 dark:border-green-600 rounded">
                                <p className="text-sm text-green-700 dark:text-green-100">
                                    <strong>Novo arquivo:</strong> {contractFiles.value[0].name}
                                </p>
                            </div>
                        )}
                    </div>

                    <ArtifactFilesUpload
                        artifactFiles={artifactFiles}
                        existingFiles={existingArtifactFiles}
                        removedFiles={removedArtifactFiles}
                        onRemoveExisting={(file) => setRemovedArtifactFiles([...removedArtifactFiles, file])}
                        onRestoreExisting={(file) => setRemovedArtifactFiles(removedArtifactFiles.filter(f => f !== file))}
                        creditOperationId={credit_operation_id || ''}
                        onDownload={handleDownload}
                    />

                    <CollateralsTabManager
                        collaterals={form.value.collaterals}
                        onChange={(collaterals) => {
                            form.value = { ...form.value, collaterals };
                            setHasUnsavedChanges(true);
                        }}
                        disabled={isLoading.value}
                        receivablesSlot={
                            <ReceivablesManager
                                receivables={form.value.collaterals.assets.receivables?.items || []}
                                currentPage={currentPage}
                                itemsPerPage={itemsPerPage}
                                onPageChange={setCurrentPage}
                                onRowClick={(receivable) => {
                                    setSelectedReceivable(receivable);
                                    setIsReceivableModalOpen(true);
                                }}
                                onReceivablesUpdate={(updatedReceivables, spreadsheetProcessed, spreadsheetItemsCount) => {
                                    form.value = {
                                        ...form.value,
                                        collaterals: {
                                            ...form.value.collaterals,
                                            assets: {
                                                ...form.value.collaterals.assets,
                                                receivables: {
                                                    items: updatedReceivables,
                                                    total: form.value.collaterals.assets.receivables?.total || 0,
                                                    quantity: form.value.collaterals.assets.receivables?.quantity || 0,
                                                    spreadsheet_processed: spreadsheetProcessed,
                                                    spreadsheet_items_count: spreadsheetItemsCount,
                                                    overcollateral: form.value.collaterals.assets.receivables?.overcollateral ?? null,
                                                }
                                            }
                                        }
                                    };
                                    setHasUnsavedChanges(true);
                                }}
                                spreadsheetProcessed={form.value.collaterals.assets.receivables?.spreadsheet_processed || false}
                                onValidationChange={setHasInvalidDueDates}
                                overcollateral={form.value.collaterals.assets.receivables?.overcollateral}
                                onOvercollateralChange={(value) => { form.value = { ...form.value, collaterals: { ...form.value.collaterals, assets: { ...form.value.collaterals.assets, receivables: { items: form.value.collaterals.assets.receivables?.items || [], total: form.value.collaterals.assets.receivables?.total || 0, quantity: form.value.collaterals.assets.receivables?.quantity || 0, overcollateral: value } } } }; setHasUnsavedChanges(true); }}
                                overcollateralError={getErrorMessage(errorFields.value, "overcollateral")}
                                convertButton={
                                    <ConvertReceivablesToInstallment
                                        receivables={form.value.collaterals.assets.receivables?.items || []}
                                        onConvert={(installments) => {
                                            const lastDueDate = installments.length > 0
                                                ? installments[installments.length - 1].dueDate
                                                : undefined;
                                            setLastInstallmentDueDate(lastDueDate);
                                            form.value = {
                                                ...form.value,
                                                installments,
                                                expiration_date: lastDueDate || form.value.expiration_date
                                            };
                                            setHasUnsavedChanges(true);
                                        }}
                                        disabled={isLoading.value}
                                    />
                                }
                                reimportButton={
                                    <ReimportReceivablesButton
                                        sequencialNumber={form.value.sequencial_number || 0}
                                        amendmentNumber={amendmentNumber}
                                        onReimported={(data) => handleJsonDataLoaded(data as unknown as JsonUploadData)}
                                        contractStatus={form.value.status}
                                    />
                                }
                            />
                        }
                    />
                </div>

                <PersonSearchModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSelect={handleSelectPerson}
                />

                {/* Modal de criação de produto */}
                {productManagement.isProductModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold dark:text-white">Novo Produto</h3>
                                <button type="button"
                                    onClick={() => {
                                        productManagement.setIsProductModalOpen(false);
                                        productManagement.setNewProduct({ name: '', description: '', quality_standard: '' });
                                    }}
                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                >
                                    <FaTimes />
                                </button>
                            </div>
                            <div className="space-y-4">
                                <TextEdit
                                    label="Nome"
                                    value={productManagement.newProduct.name}
                                    onChange={(e) => productManagement.setNewProduct({ ...productManagement.newProduct, name: e.target.value })}
                                />
                                <div>
                                    <label htmlFor="product-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descrição</label>
                                    <textarea
                                        id="product-description"
                                        rows={4}
                                        value={productManagement.newProduct.description}
                                        onChange={(e) => productManagement.setNewProduct({ ...productManagement.newProduct, description: e.target.value })}
                                        className="block w-full text-sm text-gray-900 dark:text-gray-100 bg-transparent border border-gray-300 dark:border-gray-600 rounded p-2 focus:outline-none focus:ring-0 focus:border-blue-600 dark:focus:border-blue-400 resize-none"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="product-quality-standard" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Padrão de Qualidade</label>
                                    <textarea
                                        id="product-quality-standard"
                                        rows={4}
                                        value={productManagement.newProduct.quality_standard}
                                        onChange={(e) => productManagement.setNewProduct({ ...productManagement.newProduct, quality_standard: e.target.value })}
                                        className="block w-full text-sm text-gray-900 dark:text-gray-100 bg-transparent border border-gray-300 dark:border-gray-600 rounded p-2 focus:outline-none focus:ring-0 focus:border-blue-600 dark:focus:border-blue-400 resize-none"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 mt-6">
                                <button type="button"
                                    onClick={() => {
                                        productManagement.setIsProductModalOpen(false);
                                        productManagement.setNewProduct({ name: '', description: '', quality_standard: '' });
                                    }}
                                    className="px-4 py-2 text-gray-600 bg-gray-200 rounded hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                                    disabled={productManagement.isCreatingProduct}
                                >
                                    Cancelar
                                </button>
                                <button type="button"
                                    onClick={productManagement.handleCreateProduct}
                                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={productManagement.isCreatingProduct}
                                >
                                    {productManagement.isCreatingProduct ? 'Criando...' : 'Criar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </PageForm>
            <ConfirmationModal
                isOpen={isCancelModalOpen}
                onCancel={() => setIsCancelModalOpen(false)}
                onConfirm={handleCancelContract}
                id="cancel-contract-modal"
                title="Cancelar Contrato"
                text="Tem certeza que deseja cancelar este contrato?"
            />
            <ConfirmationModal
                isOpen={isFinishModalOpen}
                onCancel={() => setIsFinishModalOpen(false)}
                onConfirm={handleFinishContract}
                id="finish-contract-modal"
                title="Finalizar Contrato"
                text="Ao finalizar, o contrato será finalizado e o registro será solicitado automaticamente (B3, Pismo e Dimensa). Deseja continuar?"
            />
            <ReceivableModalView
                isOpen={isReceivableModalOpen}
                onClose={() => {
                    setIsReceivableModalOpen(false);
                    setSelectedReceivable(null);
                }}
                receivable={selectedReceivable}
            />
            <AmendmentModal
                isOpen={isAmendmentModalOpen}
                onClose={() => setIsAmendmentModalOpen(false)}
                creditOperationId={credit_operation_id || ''}
                onSuccess={() => { navigate('/contracts/0/100/id/ASC/s=;'); }}
            />
        </Fragment>);
};

export default AcknowledgementDebtPage;